import { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  ApprovalErrorFields,
  BookingApproveFormFieldsEnum,
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeader,
  MuiButton,
  useApprovalErrorFields,
} from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import {
  BookingCellId,
  BookingConflictErrorType,
  bulkApprovalBookingConflictsAtom,
  DateYMDString,
  getBookingCalendarCellId,
  IApprovalBookingConflict,
  IDialogContainerProps,
  Log,
  SimpleResult,
  simpleResultFalse,
  TResolveFn,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { bookingBulkSetConflictApprovalReasonValidationSchema } from "./booking-bulk-set-conflict-approval-reason.validation";

interface IGetOtherConflictsThatWillBeApprovedArgs {
  id: number;
  date: DateYMDString;
  errorType: BookingConflictErrorType;
  passengerId: number;
  bulkApprovalBookingConflicts: Map<BookingCellId, IApprovalBookingConflict[]>;
}

const getOtherConflictsThatWillBeApproved = ({
  date,
  bulkApprovalBookingConflicts,
  passengerId,
}: IGetOtherConflictsThatWillBeApprovedArgs): IApprovalBookingConflict[] => {
  const bookingCellId = getBookingCalendarCellId(passengerId, date);
  return bulkApprovalBookingConflicts.get(bookingCellId) ?? [];
};

interface IBookingBulkSetConflictApprovalReasonDialog extends IDialogContainerProps {
  id: number;
  passengerId: number;
  passengerFullName: string;
  date: DateYMDString;
  errorType: BookingConflictErrorType;
  resolveFn: TResolveFn;
}

export const BookingBulkSetConflictApprovalReasonDialog: FC<
  IBookingBulkSetConflictApprovalReasonDialog
> = ({
  onClose,
  id,
  passengerId,
  passengerFullName,
  errorType,
  date,
  resolveFn,
}) => {
  const resolver = useYupValidationResolver(
    bookingBulkSetConflictApprovalReasonValidationSchema,
  );
  const methods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {},
    resolver,
  });
  const {
    getValues,
    setValue,
    formState: { isValid },
  } = methods;
  const { t } = useTranslation();
  const bulkApprovalBookingConflicts = useRecoilValue(
    bulkApprovalBookingConflictsAtom,
  );
  const conflictsThatWillBeApproved = getOtherConflictsThatWillBeApproved({
    id,
    errorType,
    date,
    bulkApprovalBookingConflicts,
    passengerId,
  });

  const { reasonCategoryDropdownItems, reasonDropdownItems } =
    useApprovalErrorFields({ getValues, setValue });

  const onCancel = (): void => {
    resolveFn(simpleResultFalse);
    onClose();
  };

  const onApprove = (): void => {
    if (!isValid) {
      Log.errorAndToast(t("errors.invalidBookingApproval"));
      return;
    }

    const values = getValues();

    const approvalReasonToResult = reasonDropdownItems.find(
      ({ value }) =>
        Number(value) ===
        Number(values[BookingApproveFormFieldsEnum.APPROVAL_REASON_ID]),
    );
    const approver = values[BookingApproveFormFieldsEnum.APPROVER];

    resolveFn(
      new SimpleResult({
        success: true,
        data: {
          approvalReason: approvalReasonToResult,
          approver,
        },
      }),
    );
    Log.successAndToast(t("booking.APPROVAL_REASON_APPROVED_SUCCESSFULLY"));
    onClose();
  };

  return (
    <ModalContainerStyled>
      <ModalHeader onClose={onClose}>
        {t("booking.APPROVAL_REASON")}
      </ModalHeader>
      <ModalContentStyled $showContent>
        <header>
          <h4 className="ApprovalReasonDialog-Title">
            <Trans
              i18nKey="booking.APPROVE_DUE_THE_REASON"
              components={{
                passenger: <span style={{ color: rydeColors.purpleTitle }} />,
              }}
              values={{ passengerFullName }}
            />
          </h4>
          {conflictsThatWillBeApproved.length > 0 && (
            <ul className="ApprovalReasonDialog-Conflicts">
              {conflictsThatWillBeApproved.map((conflict, i) => (
                <li key={`${conflict.errorType}_${i}`}>
                  {t(`booking.conflictApprove.${conflict.errorType}`)}
                </li>
              ))}
            </ul>
          )}
        </header>
        <FormProvider {...methods}>
          <ApprovalErrorFields
            reasonDropdownItems={reasonDropdownItems}
            reasonCategoryDropdownItems={reasonCategoryDropdownItems}
          />
        </FormProvider>
      </ModalContentStyled>
      <ModalFooterStyled>
        <MuiButton muiVariant="outlined" onClick={onCancel}>
          {t("general.CANCEL")}
        </MuiButton>
        <MuiButton onClick={onApprove} disabled={!isValid}>
          {t("general.APPROVE")}
        </MuiButton>
      </ModalFooterStyled>
    </ModalContainerStyled>
  );
};
